import React, { useEffect, useState } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  CloseButton,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { giftCardCategory } from "@/utils/giftCardUtils";
import { Category } from "@/dto/category";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";

export const SecondaryServicesNavigationBar = ({
  displayCategories,
  moreCategories,
  retailCategories,
  serviceCategories,
  scanToOrderInfo,
}: any) => {
  const [currentCategory, setCurrentCategoryState] = useState("");

  useEffect(() => {
    const param = window.location.hash.substring(1);
    setCurrentCategoryState(param);
  }, []);

  const pathName = usePathname();
  const [filteredCategories, setFilteredCategories] = useState(
    displayCategories.filter((c: Category) => c.id !== giftCardCategory.id)
  );
  const [filteredMoreCategories, setFilteredMoreCategories] =
    useState(moreCategories);

  useEffect(() => {
    setCurrentCategoryState(""); // make sure when page changes, there is no category selected
    if (pathName.includes("/services")) {
      const renamedCategories = renameUncategorisedCategory(serviceCategories);
      setFilteredCategories(
        renamedCategories
          .slice(0, 5)
          .filter((category: Category) => category.id !== giftCardCategory.id)
      );
      setFilteredMoreCategories(
        renamedCategories
          .slice(5)
          .filter((cat: Category) => cat.id !== giftCardCategory.id) || []
      );
    }

    if (pathName.includes("/shop")) {
      const renamedCategories = renameUncategorisedCategory(retailCategories);
      setFilteredCategories(
        renamedCategories
          .slice(0, 5)
          .filter((category: Category) => category.id !== giftCardCategory.id)
      );
      setFilteredMoreCategories(
        renamedCategories
          .slice(5)
          .filter((cat: Category) => cat.id !== giftCardCategory.id)
      );
    }
  }, [pathName]);

  const handleCategoryClick = (categoryName: string) => {
    setCurrentCategoryState(categoryName);
  };

  return (
    <div
      className={[
        "hidden",
        "sm:flex",
        "items-center",
        "sm:w-full",
        "sm:px-8",
        "pb-[10px]",
        "pt-6",
      ].join(" ")}
    >
      {filteredCategories.map((c: { id: string; name: string }) => {
        let queryParams = "";
        if (scanToOrderInfo) {
          queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
        }
        const url = `${pathName}${queryParams}#${c.name}`;
        return (
          <Link
            key={c.id}
            onClick={() => handleCategoryClick(c.name)}
            href={url}
            className={[
              "leading-6",
              "text-gray-900",
              "mr-9",
              "hover:bg-gray-50",
              currentCategory === c.name ? "font-bold underline" : "",
              "font-normal text-base leading-6",
            ].join(" ")}
          >
            {c.name}
          </Link>
        );
      })}
      {filteredMoreCategories.length > 0 && (
        <Popover className="relative">
          <PopoverButton
            className={[
              "flex",
              "items-center",
              "gap-x-2",
              "leading-6",
              "px-4",
              "border-transparent",
              "text-gray-900",
              "hover:bg-gray-50",
              "font-normal text-base leading-6",
            ].join(" ")}
          >
            More
            <ChevronDownIcon
              aria-hidden="true"
              className="h-5 w-5 flex-none text-gray-900"
            />
          </PopoverButton>
          <PopoverPanel
            transition
            className="absolute -left-8 top-full z-20 mt-3 w-screen max-w-[400px] overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[500px] overflow-y-auto p-3"
          >
            <div className="p-3">
              {filteredMoreCategories.map((c: { id: string; name: string }) => (
                <div
                  key={c.id}
                  className="group relative flex items-center gap-x-6 rounded-lg p-3 text-sm leading-6 hover:bg-gray-50"
                >
                  <div className="flex-auto">
                    <Link
                      href={`#${c.name}`}
                      className={[
                        "block text-gray-900",
                        "font-normal text-base leading-6",
                      ].join(" ")}
                    >
                      <CloseButton>
                        {" "}
                        {c.name}
                        <span className="absolute inset-0" /> 
                      </CloseButton>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </PopoverPanel>
        </Popover>
      )}
    </div>
  );
};

const renameUncategorisedCategory = (categories: Category[]) =>
  categories.map((cat) =>
    cat.id === "uncategorised" ? { ...cat, name: "Uncategorized" } : cat
  );
